<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue"
import {BreadcrumbPage} from "~/components/ui/breadcrumb"

const props = defineProps<{
  text?: string
  /**
   * Animate only what changed: letters shared with the previous text from its start stay put, only
   * the new tail types itself out, and the width slides from the old size to the new one — e.g.
   * «Антон думает…» → «Антон печатает…» animates just «печатает…». Without it any change replays
   * the whole entrance (the right thing for titles, where the text is a different thing each time).
   */
  incremental?: boolean
  /** The «processing» shimmer (style.css) on the letters — not on the root, whose `animation` sizes it. */
  shimmer?: boolean
}>()

interface Letter {
  key: string
  char: string
  value: string
  delay: string
}

const LETTER_STEP_MS = 36
let generation = 0
const letters = ref<Letter[]>([])

function lettersFrom(chars: string[], startIndex: number): Letter[] {
  generation++
  return chars.map((char, offset) => ({
    key: `${generation}-${startIndex + offset}`,
    char,
    value: char === ' ' ? '\u00A0' : char,
    delay: `${offset * LETTER_STEP_MS}ms`,
  }))
}

watch(() => props.text, (text) => {
  const chars = Array.from(text ?? '')
  if (!props.incremental) {
    letters.value = lettersFrom(chars, 0)
    return
  }
  // Keep the letters (same elements, already landed) of the common start; only the rest is new.
  const previous = letters.value
  let prefix = 0
  while (prefix < previous.length && prefix < chars.length && previous[prefix]!.char === chars[prefix]) prefix++
  letters.value = [...previous.slice(0, prefix), ...lettersFrom(chars.slice(prefix), prefix)]
}, {immediate: true})

// Measured on the rendered letters themselves (their padding already holds the edge buffer): split
// into inline-blocks they lose kerning and run wider than the same text set as one run, so a separate
// plain-text measure undershot and clipped the last letters («Проверя», «Думае»).
// A title's letters run is inline (so the root's `text-overflow: ellipsis` can cut it), hence its box,
// not `scrollWidth`, which an inline element doesn't have.
const lettersEl = ref<HTMLElement | null>(null)
const textWidth = ref(0)
const readWidth = () => Math.ceil(lettersEl.value?.getBoundingClientRect().width ?? 0) + 1

// A title's ellipsis only once the width has landed: while it's still growing the text is "cut" on
// purpose, and an ellipsis riding the edge of the entrance would spoil it. After that it shows only when
// the space really is too small (the breadcrumbs gave way all they could, see BreadcrumbList.vue).
// Incremental text (an activity line) keeps its plain clip.
const settled = ref(false)
watch(() => props.text, () => { settled.value = false }, { flush: "pre" })

function onAnimationEnd(event: AnimationEvent) {
  if (event.target === event.currentTarget && event.animationName.startsWith("animated-breadcrumb-page-width")) settled.value = true
}

const motionStyle = computed(() => ({
  '--breadcrumb-page-width': `${textWidth.value}px`,
  '--breadcrumb-page-duration': `${Math.max(640, letters.value.length * LETTER_STEP_MS + 520)}ms`,
}))

const measureText = async () => {
  if (props.incremental) return
  await nextTick()
  textWidth.value = readWidth()
}

onMounted(measureText)
watch(() => props.text, measureText)

// Incremental mode never sets a width of its own: the element keeps its natural (auto) width, so the
// text can't end up clipped by a stale or wrong measurement (a measured width undershot in the
// webview and cut off «Проверя», «Думае»). The size change is animated FLIP-style instead — the
// width before the change, the natural width after it, and a Web Animation between the two.
const rootRef = ref<{ $el: HTMLElement } | null>(null)
const WIDTH_EASING = "cubic-bezier(0.2, 0.9, 0.22, 1)"
let widthAnimation: Animation | null = null

function animateWidth(from: number) {
  const el = rootRef.value?.$el
  if (!el || typeof el.animate !== "function") return
  widthAnimation?.cancel()
  const to = el.offsetWidth
  if (Math.abs(to - from) < 1) return
  widthAnimation = el.animate(
      [{ inlineSize: `${from}px` }, { inlineSize: `${to}px` }],
      { duration: from === 0 ? 640 : 420, easing: WIDTH_EASING },
  )
}

onMounted(() => {
  if (props.incremental) animateWidth(0)
})
watch(() => props.text, () => {
  if (!props.incremental) return
  const el = rootRef.value?.$el
  // Mid-animation offsetWidth is the animated (current) width — exactly where the next one starts.
  const from = el?.offsetWidth ?? 0
  void nextTick(() => animateWidth(from))
}, { flush: "pre" })
onBeforeUnmount(() => widthAnimation?.cancel())
</script>

<template>
  <BreadcrumbPage
      ref="rootRef"
      :key="incremental ? undefined : text"
      class="animated-breadcrumb-page"
      :class="[incremental && 'animated-breadcrumb-page--incremental', settled && 'animated-breadcrumb-page--settled']"
      :aria-label="text"
      :style="motionStyle"
      @animationend="onAnimationEnd"
  >
    <span
        :key="incremental ? undefined : text"
        ref="lettersEl"
        aria-hidden="true"
        class="animated-breadcrumb-page__letters"
        :class="shimmer && 'processing-shimmer'"
    >
      <span
          v-for="letter in letters"
          :key="letter.key"
          class="animated-breadcrumb-page__letter"
          :style="{ animationDelay: letter.delay }"
      >
        {{ letter.value }}
      </span>
    </span>
  </BreadcrumbPage>
</template>

<style scoped>
.animated-breadcrumb-page {
  --breadcrumb-page-edge-buffer: 0.28em;

  display: inline-block;
  position: relative;
  inline-size: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: clip;
  animation: animated-breadcrumb-page-width var(--breadcrumb-page-duration, 640ms) cubic-bezier(0.2, 0.9, 0.22, 1) forwards;
}

.animated-breadcrumb-page--incremental {
  display: inline-flex;
  animation: none;
  inline-size: auto;
}

/* Cut with an ellipsis — the letters are inline-blocks, so whole letters give way to the «…». */
.animated-breadcrumb-page--settled {
  text-overflow: ellipsis;
}

.animated-breadcrumb-page__letters {
  display: inline;
  padding-inline-end: var(--breadcrumb-page-edge-buffer);
  white-space: nowrap;
}

/* Incremental: one block of letters, as before — the «processing» shimmer is a mask on this element. */
.animated-breadcrumb-page--incremental .animated-breadcrumb-page__letters {
  display: inline-flex;
  inline-size: max-content;
  flex-shrink: 0;
}

.animated-breadcrumb-page__letter {
  display: inline-block;
  opacity: 0;
  color: color-mix(in srgb, currentColor 34%, transparent);
  transform: translateY(0.72em) scale(0.72, 1.28);
  transform-origin: 50% 100%;
  filter: blur(5px);
  animation: animated-breadcrumb-page-blob-type 640ms cubic-bezier(0.2, 0.9, 0.22, 1) forwards;
  will-change: transform, opacity, filter, color;
}

/* Touch devices (iOS WKWebView in particular): a per-letter `filter: blur()` keyframe animation
   doesn't reliably finish there — letters get stuck half-blurred, washed out and misaligned next to
   the ones that did land, which is how "<name> печатает..." showed up on phones. The blob/blur
   effect stays on desktop; here each letter just rises and fades in. */
@media (pointer: coarse) {
  .animated-breadcrumb-page__letter {
    color: currentColor;
    transform: translateY(0.35em);
    filter: none;
    animation-name: animated-breadcrumb-page-rise;
    will-change: transform, opacity;
  }
}

@keyframes animated-breadcrumb-page-rise {
  from {
    opacity: 0;
    transform: translateY(0.35em);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes animated-breadcrumb-page-width {
  from {
    inline-size: 0;
  }

  to {
    inline-size: var(--breadcrumb-page-width);
  }
}

@keyframes animated-breadcrumb-page-blob-type {
  0% {
    opacity: 0;
    color: color-mix(in srgb, currentColor 34%, transparent);
    transform: translateY(0.72em) scale(0.72, 1.28);
    filter: blur(5px);
  }

  44% {
    opacity: 0.82;
    color: color-mix(in srgb, currentColor 64%, transparent);
    transform: translateY(-0.16em) scale(1.18, 0.82);
    filter: blur(1.2px);
  }

  66% {
    opacity: 1;
    color: currentColor;
    transform: translateY(0.05em) scale(0.94, 1.06);
    filter: blur(0);
  }

  100% {
    opacity: 1;
    color: currentColor;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}
</style>
